import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCalibration,
  isCalibrated,
  saveCalibration,
  type CalibrationData,
} from "../utils/calibration";

const CARD_WIDTH_MM = 85.6;
const CARD_HEIGHT_MM = 53.98;
const CARD_ASPECT_RATIO = CARD_HEIGHT_MM / CARD_WIDTH_MM;
const CARD_CORNER_RADIUS_MM = 3.18;
const BORDER_THICKNESS_PX = 18;
const MIN_INNER_WIDTH_PX = 160;
const MIN_SLIDER_SPAN_PX = 120;
const OUTER_CORNER_RADIUS_PX = 16;
const MIN_LINE_LENGTH_PX = 180;
const FIXED_LINE_LENGTH_PX = 320;

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CalibrationPage() {
  const navigate = useNavigate();

  const getMaxInnerWidthPx = useCallback(() => {
    const safeByWidth = Math.max(MIN_INNER_WIDTH_PX, window.innerWidth - 72);
    const safeByHeight = Math.max(
      MIN_INNER_WIDTH_PX,
      (window.innerHeight - 240) / CARD_ASPECT_RATIO,
    );
    const computedMax = Math.max(
      MIN_INNER_WIDTH_PX,
      Math.min(720, safeByWidth, safeByHeight),
    );
    return Math.max(MIN_INNER_WIDTH_PX + MIN_SLIDER_SPAN_PX, computedMax);
  }, []);

  const clampInnerWidth = useCallback(
    (value: number) => {
      const safeValue = Number.isFinite(value) ? value : MIN_INNER_WIDTH_PX;
      const max = getMaxInnerWidthPx();
      const safeMax = Number.isFinite(max)
        ? max
        : MIN_INNER_WIDTH_PX + MIN_SLIDER_SPAN_PX;
      return Math.min(safeMax, Math.max(MIN_INNER_WIDTH_PX, safeValue));
    },
    [getMaxInnerWidthPx],
  );

  const [innerWidthPx, setInnerWidthPx] = useState<number>(() =>
    Math.round(Math.min(420, window.innerWidth * 0.7)),
  );
  const [isSaved, setIsSaved] = useState(false);
  const [existingCalibration, setExistingCalibration] =
    useState<CalibrationData | null>(null);
  const [lineMeasuredMm, setLineMeasuredMm] = useState<string>("100");
  const [lineError, setLineError] = useState<string | null>(null);

  useEffect(() => {
    if (isCalibrated()) {
      const calibration = getCalibration();
      setExistingCalibration(calibration);
      setInnerWidthPx(clampInnerWidth(calibration.ppmm * CARD_WIDTH_MM));
    } else {
      setInnerWidthPx((current) => clampInnerWidth(current));
    }
  }, [clampInnerWidth]);

  useEffect(() => {
    const handleResize = () => {
      setInnerWidthPx((current) => clampInnerWidth(current));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampInnerWidth]);

  const innerHeightPx = innerWidthPx * CARD_ASPECT_RATIO;
  const ppmm = useMemo(() => innerWidthPx / CARD_WIDTH_MM, [innerWidthPx]);
  const innerCornerRadiusPx = useMemo(
    () => (CARD_CORNER_RADIUS_MM / CARD_WIDTH_MM) * innerWidthPx,
    [innerWidthPx],
  );
  const sliderMax = Math.floor(getMaxInnerWidthPx());
  const lineSliderMax = Math.max(
    MIN_LINE_LENGTH_PX + 100,
    Math.floor(window.innerWidth - 96),
  );
  const fixedLineLengthPx = Math.min(
    lineSliderMax,
    Math.max(MIN_LINE_LENGTH_PX, FIXED_LINE_LENGTH_PX),
  );

  const handleSliderChange = (value: string) => {
    const next = Number(value);
    if (!Number.isFinite(next)) return;
    setIsSaved(false);
    setInnerWidthPx(clampInnerWidth(next));
  };

  const handleSaveByPpmm = (nextPpmm: number, method: "card" | "line") => {
    const data: CalibrationData = {
      screenWidthMm: window.innerWidth / nextPpmm,
      screenHeightMm: window.innerHeight / nextPpmm,
      ppmm: nextPpmm,
      calibratedAt: new Date().toISOString(),
      method,
    };

    saveCalibration(data);
    setExistingCalibration(data);
    setIsSaved(true);
  };

  const handleSave = () => {
    if (!Number.isFinite(ppmm) || ppmm <= 0) return;
    handleSaveByPpmm(ppmm, "card");
  };

  const handleSaveByLine = () => {
    const measuredMm = Number(lineMeasuredMm);
    if (!Number.isFinite(measuredMm) || measuredMm <= 0) {
      setLineError("Please enter a valid measured length in mm.");
      return;
    }

    const linePpmm = fixedLineLengthPx / measuredMm;
    if (!Number.isFinite(linePpmm) || linePpmm <= 0) {
      setLineError("Failed to calculate calibration value from the line.");
      return;
    }

    setLineError(null);
    handleSaveByPpmm(linePpmm, "line");
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            画面キャリブレーション
          </h1>
          <p className="mt-2 text-gray-700">
            カード方式と線の実測方式のどちらか一方で保存すれば有効です。
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              カード方式
            </h2>
            <ol className="list-decimal list-inside space-y-1 text-gray-700">
              <li>カードを白枠に合わせる</li>
              <li>スライダーでサイズを一致させる</li>
              <li>保存する</li>
            </ol>
          </div>

          <div className="mb-6">
            <div className="flex justify-center py-4">
              <div
                className="relative bg-black shadow-lg"
                style={{
                  padding: `${BORDER_THICKNESS_PX}px`,
                  borderRadius: `${OUTER_CORNER_RADIUS_PX}px`,
                }}
              >
                <div
                  className="relative bg-white"
                  style={{
                    width: `${innerWidthPx}px`,
                    height: `${innerHeightPx}px`,
                    borderRadius: `${innerCornerRadiusPx}px`,
                  }}
                  aria-label="ISO card 85.60mm x 53.98mm"
                />
              </div>
            </div>
            <p className="text-center text-sm text-gray-600">
              白枠を実物カードに合わせてください
            </p>
          </div>

          <div className="mb-6">
            <label
              htmlFor="frameSizeSlider"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              白枠サイズ調整
            </label>
            <input
              id="frameSizeSlider"
              type="range"
              min={MIN_INNER_WIDTH_PX}
              max={sliderMax}
              step={1}
              value={Math.round(innerWidthPx)}
              onChange={(e) => handleSliderChange(e.target.value)}
              className="w-full"
            />
          </div>

          <div className="mb-6 p-4 bg-blue-50 rounded-lg text-sm text-gray-700">
            <p>
              現在の白枠: {innerWidthPx.toFixed(1)}px x{" "}
              {innerHeightPx.toFixed(1)}px
            </p>
            <p>尺度: 1mm あたり {ppmm.toFixed(3)} px</p>
          </div>

          <div className="mb-6">
            <button
              onClick={handleSave}
              className="w-full py-3 text-lg font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              カード方式で保存
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              線の実測方式
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              下の線を定規で測り、実測した長さ(mm)を入力して保存します。
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center py-4">
                <div
                  className="h-0.5 bg-gray-900"
                  style={{ width: `${fixedLineLengthPx}px` }}
                  aria-label="Measured line"
                />
              </div>
              <p className="text-xs text-gray-500 text-center">
                この線の長さを定規で測ってください
              </p>
              <p className="text-xs text-gray-500 text-center mt-1">
                固定表示長: {fixedLineLengthPx.toFixed(0)} px
              </p>
            </div>

            <div className="mb-4">
              <label
                htmlFor="lineMeasuredMm"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                実測した線の長さ(mm)
              </label>
              <input
                id="lineMeasuredMm"
                type="number"
                min={1}
                step={0.1}
                value={lineMeasuredMm}
                onChange={(e) => {
                  setLineMeasuredMm(e.target.value);
                  setLineError(null);
                  setIsSaved(false);
                }}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            {lineError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {lineError}
              </div>
            )}

            <button
              onClick={handleSaveByLine}
              className="w-full py-3 text-lg font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
            >
              線の実測値で保存
            </button>
          </div>

          {isSaved && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-center font-medium">
                キャリブレーションを保存しました。
              </p>
            </div>
          )}

          {existingCalibration && existingCalibration.calibratedAt && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
              <h3 className="font-semibold text-gray-900 mb-2">
                保存済みの設定情報
              </h3>
              <p>保存日時: {formatDate(existingCalibration.calibratedAt)}</p>
              <p>尺度: {existingCalibration.ppmm.toFixed(3)} px/mm</p>
              <p>
                方式:{" "}
                {existingCalibration.method === "line"
                  ? "線の実測"
                  : "カード合わせ"}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-gray-200 border border-gray-300 rounded text-gray-700 hover:bg-gray-300 transition-colors font-medium"
          >
            戻る
          </button>
        </div>
      </div>
    </div>
  );
}
